import { Field } from '@/components/ui/field';
import { Alert } from '@/components/ui/alert';
import { InputGroup } from '@/components/ui/input-group';
import {
  NumberInputControl,
  NumberInputField,
  NumberInputRoot,
} from '@/components/ui/number-input';
import { RadioCardItem, RadioCardLabel, RadioCardRoot } from '@/components/ui/radio-card';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/auth-context';
import { invalidateBrandSpaceProgramOptions } from '@/hooks/queries/use-brand-space-program-options-query';
import { updateProgram } from '@/services/affiliate-programs-service';
import { toaster } from '@/components/ui/toaster';
import {
  Box,
  Button,
  createListCollection,
  Flex,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  BrandAccountType,
  CommissionTypes,
  MAX_COMMISSION_CATEGORY_RULES,
  ProgramMode,
  type CommissionCategoryRate,
  type ApiResponse,
  type Program,
} from '@fluenceur/types';
import { zodResolver } from '@hookform/resolvers/zod';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { DollarCircle, InfoCircle, PercentageCircle, Plus, Trash } from 'iconoir-react';
import type { AxiosError } from 'axios';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';

// Validation schema
const rewardsFormSchema = (
  t: (key: string) => string,
  usesCategoryGrid: boolean,
  supportsFirstOrderCommission: boolean
) =>
  z
    .object({
      applyCommission: z.boolean(),
      commissionType: z
        .enum([CommissionTypes.FixedAmountPerOrder, CommissionTypes.Percentage])
        .nullable()
        .optional(),
      commissionValue: z.number().nullable().optional(),
      applyFirstOrderCommission: z.boolean().optional(),
      firstOrderCommissionTrigger: z
        .enum(['affiliate_first_order', 'customer_first_order'])
        .optional(),
      firstOrderCommissionType: z
        .enum([CommissionTypes.FixedAmountPerOrder, CommissionTypes.Percentage])
        .nullable()
        .optional(),
      firstOrderCommissionValue: z.number().nullable().optional(),
      commissionCategories: z
        .array(
          z.object({
            key: z.string().trim().min(1),
            label: z.string().trim().min(1),
            type: z.enum([CommissionTypes.FixedAmountPerOrder, CommissionTypes.Percentage]),
            value: z.number().positive(),
          })
        )
        .max(MAX_COMMISSION_CATEGORY_RULES, t('programs.validation.commissionCategoriesMax'))
        .optional(),
    })
    .superRefine((input, ctx) => {
      if (input.applyCommission) {
        if (usesCategoryGrid) {
          if (!input.commissionCategories?.length) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['commissionCategories'],
              message: t('programs.validation.commissionCategoriesRequired'),
            });
            return;
          }
          const normalizedKeys = input.commissionCategories.map(({ key }) =>
            key.trim().toLowerCase()
          );
          if (new Set(normalizedKeys).size !== normalizedKeys.length) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['commissionCategories'],
              message: t('programs.validation.commissionCategoriesUnique'),
            });
          }
          input.commissionCategories.forEach((category, index) => {
            if (category.type === CommissionTypes.Percentage && category.value > 100) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['commissionCategories', index, 'value'],
                message: t('programs.validation.commissionPercentageMax'),
              });
            }
          });
          return;
        }
        if (!input.commissionType) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['commissionType'],
            message: t('programs.validation.commissionTypeRequired'),
          });
        }
        if (!input.commissionValue || input.commissionValue <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['commissionValue'],
            message: t('programs.validation.commissionValuePositive'),
          });
        }
        if (
          input.commissionType === CommissionTypes.Percentage &&
          input.commissionValue &&
          input.commissionValue > 100
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['commissionValue'],
            message: t('programs.validation.commissionPercentageMax'),
          });
        }
        if (supportsFirstOrderCommission && input.applyFirstOrderCommission) {
          if (!input.firstOrderCommissionType) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['firstOrderCommissionType'],
              message: t('programs.validation.commissionTypeRequired'),
            });
          }
          if (!input.firstOrderCommissionValue || input.firstOrderCommissionValue <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['firstOrderCommissionValue'],
              message: t('programs.validation.commissionValuePositive'),
            });
          }
          if (
            input.firstOrderCommissionType === CommissionTypes.Percentage &&
            input.firstOrderCommissionValue &&
            input.firstOrderCommissionValue > 100
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['firstOrderCommissionValue'],
              message: t('programs.validation.commissionPercentageMax'),
            });
          }
        }
      }
    });

export type ProgramRewardsFormData = {
  applyCommission: boolean;
  commissionType?: CommissionTypes | null;
  commissionValue?: number | null;
  applyFirstOrderCommission?: boolean;
  firstOrderCommissionTrigger?: 'affiliate_first_order' | 'customer_first_order';
  firstOrderCommissionType?: CommissionTypes | null;
  firstOrderCommissionValue?: number | null;
  commissionCategories?: CommissionCategoryRate[];
};

interface ProgramRewardsFormProps {
  programId: string;
  programMode?: ProgramMode;
  defaultValues?: Partial<ProgramRewardsFormData>;
  onSuccess: () => void;
  onSubmitting: (isSubmitting: boolean) => void;
}

const ProgramRewardsForm = forwardRef(
  (
    { programId, programMode, defaultValues, onSuccess, onSubmitting }: ProgramRewardsFormProps,
    ref
  ) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const submittingRef = useRef(false);
    const usesCategoryGrid = user?.currentBrand?.accountType === BrandAccountType.PARTNER;
    const supportsFirstOrderCommission =
      user?.currentBrand?.accountType === BrandAccountType.SHOPIFY &&
      programMode !== ProgramMode.Recurring;
    const schema = useMemo(
      () => rewardsFormSchema(t, usesCategoryGrid, supportsFirstOrderCommission),
      [t, usesCategoryGrid, supportsFirstOrderCommission]
    );
    const currency = user?.currentBrand?.currency ?? 'EUR';

    const platformFeeModel = user?.currentBrand?.platformFeeModel;
    const platformFeeRate = user?.currentBrand?.platformFeeRate ?? 0.05;

    const commissionOptions = [
      {
        value: CommissionTypes.Percentage,
        title: t('programs.modals.editRewards.percentage'),
        icon: <PercentageCircle width={20} height={20} />,
        description: t('programs.modals.editRewards.percentageDescription'),
      },
      {
        value: CommissionTypes.FixedAmountPerOrder,
        title: t('programs.modals.editRewards.fixedAmount'),
        icon: <DollarCircle width={20} height={20} />,
        description: t('programs.modals.editRewards.fixedAmountDescription'),
      },
    ];
    const commissionTypeCollection = useMemo(
      () =>
        createListCollection({
          items: [
            {
              value: CommissionTypes.Percentage,
              label: t('programs.modals.editRewards.percentage'),
            },
            {
              value: CommissionTypes.FixedAmountPerOrder,
              label: t('programs.modals.editRewards.fixedAmount'),
            },
          ],
        }),
      [t]
    );

    const specialRewardCollection = useMemo(
      () =>
        createListCollection({
          items: [
            { value: 'none', label: t('programs.modals.editRewards.noSpecialReward') },
            {
              value: 'affiliate_first_order',
              label: t('programs.modals.editRewards.applyFirstOrderCommission'),
            },
            {
              value: 'customer_first_order',
              label: t('programs.modals.editRewards.customerFirstOrderTrigger'),
            },
          ],
        }),
      [t]
    );

    const methods = useForm<ProgramRewardsFormData>({
      resolver: zodResolver(schema),
      defaultValues: {
        ...defaultValues,
        applyCommission: defaultValues?.applyCommission ?? false,
        commissionType: defaultValues?.commissionType ?? CommissionTypes.Percentage,
        commissionValue: defaultValues?.commissionValue ?? 10,
        applyFirstOrderCommission: defaultValues?.applyFirstOrderCommission ?? false,
        firstOrderCommissionTrigger:
          defaultValues?.firstOrderCommissionTrigger ?? 'affiliate_first_order',
        firstOrderCommissionType:
          defaultValues?.firstOrderCommissionType ?? CommissionTypes.FixedAmountPerOrder,
        firstOrderCommissionValue: defaultValues?.firstOrderCommissionValue ?? 10,
        commissionCategories: usesCategoryGrid
          ? (defaultValues?.commissionCategories ?? [
              {
                key: '',
                label: '',
                type: CommissionTypes.Percentage,
                value: 0,
              },
            ])
          : undefined,
      },
    });

    const categories = useFieldArray({
      control: methods.control,
      name: 'commissionCategories',
    });

    const handleMutation = async (data: ProgramRewardsFormData) => {
      return await updateProgram(programId, data);
    };

    const mutation = useMutation<Program, AxiosError<ApiResponse>, ProgramRewardsFormData>({
      mutationFn: handleMutation,
      onSuccess: () => {
        // Invalidate specific program and programs list to refresh the data
        queryClient.invalidateQueries({
          queryKey: ['program', programId],
        });
        queryClient.invalidateQueries({
          queryKey: ['programs'],
        });
        void invalidateBrandSpaceProgramOptions(queryClient);
        toaster.success({ title: t('programs.modals.editRewards.saveSuccess') });
        onSuccess(); // Close the modal
      },
      onError: (error) => {
        // A plan refusal opens the upsell from the API client; a toast would repeat it.
        if (isPlanLimitError(error)) return;
        // The backend names the reason a grid cannot be published; the generic
        // sentence is only for a failure that carries none.
        toaster.error({
          title: error.response?.data?.message || t('programs.modals.editRewards.saveError'),
        });
      },
      onSettled: () => {
        submittingRef.current = false;
      },
    });

    useEffect(() => {
      onSubmitting(mutation.isPending);
    }, [onSubmitting, mutation.isPending]);

    const onSubmit = (data: ProgramRewardsFormData) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      // The field array registers `commissionCategories` as `[]` even when the
      // grid is not rendered, and the backend refuses an empty grid; each branch
      // therefore names exactly the fields it owns.
      mutation.mutate(
        usesCategoryGrid
          ? {
              applyCommission: data.applyCommission,
              commissionCategories: data.applyCommission ? data.commissionCategories : undefined,
            }
          : {
              applyCommission: data.applyCommission,
              commissionType: data.applyCommission ? data.commissionType : null,
              commissionValue: data.applyCommission ? data.commissionValue : null,
              ...(supportsFirstOrderCommission
                ? {
                    applyFirstOrderCommission:
                      data.applyCommission && !!data.applyFirstOrderCommission,
                    firstOrderCommissionTrigger: data.firstOrderCommissionTrigger,
                    firstOrderCommissionType: data.firstOrderCommissionType,
                    firstOrderCommissionValue: data.firstOrderCommissionValue,
                  }
                : {}),
            }
      );
    };

    // Expose the submit method to the parent using `ref`
    useImperativeHandle(ref, () => ({
      submit: methods.handleSubmit(onSubmit),
    }));

    const { register, setValue, watch, formState } = methods;
    const applyCommission = watch('applyCommission', defaultValues?.applyCommission ?? false);
    const watchedCommissionValue = watch('commissionValue');
    const commissionType = watch('commissionType');
    const applyFirstOrderCommission = watch('applyFirstOrderCommission');
    const firstOrderCommissionType = watch('firstOrderCommissionType');
    const firstOrderCommissionTrigger = watch('firstOrderCommissionTrigger');
    const isCustomerReward = firstOrderCommissionTrigger === 'customer_first_order';

    const handleTypeChange = (value: string) => {
      setValue('commissionType', value as CommissionTypes, { shouldValidate: true });
    };

    return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <VStack gap={6} align="stretch">
            {/* Apply Commission Toggle */}
            <Box>
              <Switch
                colorPalette="primary"
                checked={applyCommission}
                onCheckedChange={(e) => setValue('applyCommission', e.checked)}
              >
                {t('programs.modals.editRewards.applyCommission')}
              </Switch>
              <Text fontSize="sm" color="gray.500" mt={2}>
                {t('programs.modals.editRewards.applyCommissionDescription')}
              </Text>
            </Box>

            {applyCommission && usesCategoryGrid && (
              <VStack align="stretch" gap={4}>
                <Box>
                  <Text fontWeight="semibold">
                    {t('programs.modals.editRewards.categoryGridTitle')}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {t('programs.modals.editRewards.categoryGridDescription')}
                  </Text>
                </Box>
                {categories.fields.map((field, index) => (
                  <Box key={field.id} borderWidth="1px" rounded="lg" p={4}>
                    <VStack align="stretch" gap={3}>
                      <HStack align="start">
                        <Field
                          label={t('programs.modals.editRewards.categoryKey')}
                          invalid={!!formState.errors.commissionCategories?.[index]?.key}
                          errorText={formState.errors.commissionCategories?.[index]?.key?.message}
                        >
                          <Input
                            placeholder={t('programs.modals.editRewards.categoryKeyPlaceholder')}
                            {...register(`commissionCategories.${index}.key`)}
                          />
                        </Field>
                        <Field
                          label={t('programs.modals.editRewards.categoryLabel')}
                          invalid={!!formState.errors.commissionCategories?.[index]?.label}
                          errorText={formState.errors.commissionCategories?.[index]?.label?.message}
                        >
                          <Input
                            placeholder={t('programs.modals.editRewards.categoryLabelPlaceholder')}
                            {...register(`commissionCategories.${index}.label`)}
                          />
                        </Field>
                      </HStack>
                      <HStack align="end">
                        <Field label={t('programs.modals.editRewards.commissionType')}>
                          <SelectRoot
                            collection={commissionTypeCollection}
                            value={[
                              watch(`commissionCategories.${index}.type`) ??
                                CommissionTypes.Percentage,
                            ]}
                            onValueChange={({ value }) => {
                              const nextType = value[0] as CommissionTypes | undefined;
                              if (!nextType) return;
                              setValue(`commissionCategories.${index}.type`, nextType, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValueText />
                            </SelectTrigger>
                            <SelectContent zIndex={10001}>
                              {commissionTypeCollection.items.map((item) => (
                                <SelectItem key={item.value} item={item}>
                                  {item.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </SelectRoot>
                        </Field>
                        <Field
                          label={t('programs.modals.editRewards.commissionValue')}
                          invalid={!!formState.errors.commissionCategories?.[index]?.value}
                          errorText={formState.errors.commissionCategories?.[index]?.value?.message}
                        >
                          <Controller
                            control={methods.control}
                            name={`commissionCategories.${index}.value`}
                            render={({ field: valueField }) => (
                              <NumberInputRoot
                                min={0.01}
                                max={
                                  watch(`commissionCategories.${index}.type`) ===
                                  CommissionTypes.Percentage
                                    ? 100
                                    : 999999.99
                                }
                                step={0.01}
                                value={
                                  Number.isFinite(valueField.value) ? String(valueField.value) : ''
                                }
                                onValueChange={({ valueAsNumber }) =>
                                  valueField.onChange(
                                    Number.isNaN(valueAsNumber) ? undefined : valueAsNumber
                                  )
                                }
                                onBlur={valueField.onBlur}
                              >
                                <NumberInputField name={valueField.name} ref={valueField.ref} />
                                <NumberInputControl />
                              </NumberInputRoot>
                            )}
                          />
                        </Field>
                        <IconButton
                          type="button"
                          variant="ghost"
                          aria-label={t('programs.modals.editRewards.removeCategory')}
                          disabled={categories.fields.length === 1}
                          onClick={() => categories.remove(index)}
                        >
                          <Trash />
                        </IconButton>
                      </HStack>
                    </VStack>
                  </Box>
                ))}
                {formState.errors.commissionCategories?.root?.message && (
                  <Text color="red.500" fontSize="sm">
                    {formState.errors.commissionCategories.root.message}
                  </Text>
                )}
                <HStack alignSelf="start" gap={3}>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={categories.fields.length >= MAX_COMMISSION_CATEGORY_RULES}
                    onClick={() =>
                      categories.append({
                        key: '',
                        label: '',
                        type: CommissionTypes.Percentage,
                        value: 0,
                      })
                    }
                  >
                    <Plus />
                    {t('programs.modals.editRewards.addCategory')}
                  </Button>
                  <Text fontSize="sm" color="fg.muted">
                    {t('programs.modals.editRewards.categoryRuleCount', {
                      count: categories.fields.length,
                      max: MAX_COMMISSION_CATEGORY_RULES,
                    })}
                  </Text>
                </HStack>
              </VStack>
            )}

            {applyCommission && !usesCategoryGrid && (
              <Box>
                {/* Commission Type Selection */}
                <RadioCardRoot
                  colorPalette="primary"
                  value={commissionType || ''}
                  onValueChange={(value) => handleTypeChange(value.value ?? '')}
                >
                  <RadioCardLabel>{t('programs.modals.editRewards.commissionType')}</RadioCardLabel>
                  <HStack align="stretch" w="full">
                    {commissionOptions.map((option, index) => (
                      <RadioCardItem
                        key={index}
                        value={option.value}
                        label={
                          <Flex align="center">
                            {option.icon}
                            <Text ml={2}>{option.title}</Text>
                          </Flex>
                        }
                        description={option.description}
                      />
                    ))}
                  </HStack>
                </RadioCardRoot>

                <VStack gap={4} mt={6} align="stretch">
                  <Field
                    label={t('programs.modals.editRewards.commissionValue')}
                    invalid={!!formState.errors.commissionValue}
                    errorText={formState.errors.commissionValue?.message as string}
                  >
                    <InputGroup
                      w="full"
                      endElement={
                        <Text fontWeight="bold">
                          {commissionType === CommissionTypes.Percentage ? '%' : currency}
                        </Text>
                      }
                    >
                      <Input
                        type="number"
                        rounded="lg"
                        step="0.1"
                        placeholder={t('programs.modals.editRewards.commissionValuePlaceholder')}
                        {...register('commissionValue', {
                          setValueAs: (value) => (value === '' ? undefined : Number(value)),
                        })}
                      />
                    </InputGroup>
                    {!supportsFirstOrderCommission &&
                      platformFeeModel === 'affiliate_deducted' &&
                      watchedCommissionValue &&
                      watchedCommissionValue > 0 && (
                        <Text fontSize="sm" color="gray.500" mt={1}>
                          {t(
                            commissionType === CommissionTypes.Percentage
                              ? 'programs.modals.editRewards.affiliateNetHintPercentage'
                              : 'programs.modals.editRewards.affiliateNetHintFixed',
                            {
                              feePercent: (platformFeeRate * 100).toFixed(2).replace(/\.?0+$/, ''),
                              netValue: (watchedCommissionValue * (1 - platformFeeRate)).toFixed(2),
                            }
                          )}
                        </Text>
                      )}
                  </Field>
                </VStack>
              </Box>
            )}
            {applyCommission && supportsFirstOrderCommission ? (
              <Box borderTopWidth="1px" pt={6}>
                <Field label={t('programs.modals.editRewards.specialRewardTrigger')}>
                  <SelectRoot
                    collection={specialRewardCollection}
                    aria-label={t('programs.modals.editRewards.specialRewardTrigger')}
                    value={[
                      applyFirstOrderCommission
                        ? (firstOrderCommissionTrigger ?? 'affiliate_first_order')
                        : 'none',
                    ]}
                    onValueChange={({ value: values }) => {
                      const value = values[0];
                      if (!value) return;
                      setValue('applyFirstOrderCommission', value !== 'none', {
                        shouldDirty: true,
                      });
                      if (value !== 'none') {
                        setValue(
                          'firstOrderCommissionTrigger',
                          value as 'affiliate_first_order' | 'customer_first_order',
                          { shouldDirty: true }
                        );
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValueText />
                    </SelectTrigger>
                    <SelectContent zIndex={10001}>
                      {specialRewardCollection.items.map((item) => (
                        <SelectItem key={item.value} item={item}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </SelectRoot>
                </Field>
                {applyFirstOrderCommission ? (
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    {t(
                      isCustomerReward
                        ? 'programs.modals.editRewards.customerFirstOrderDescription'
                        : 'programs.modals.editRewards.firstOrderCommissionDescription'
                    )}
                  </Text>
                ) : null}
                {applyFirstOrderCommission ? (
                  <VStack align="stretch" gap={4} mt={4}>
                    <RadioCardRoot
                      colorPalette="primary"
                      value={firstOrderCommissionType ?? ''}
                      onValueChange={({ value }) => {
                        if (value) {
                          setValue('firstOrderCommissionType', value as CommissionTypes, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                    >
                      <RadioCardLabel>
                        {t('programs.modals.editRewards.firstOrderCommissionType')}
                      </RadioCardLabel>
                      <HStack align="stretch" w="full">
                        {commissionOptions.map((option) => (
                          <RadioCardItem
                            key={option.value}
                            value={option.value}
                            label={option.title}
                            description={option.description}
                          />
                        ))}
                      </HStack>
                    </RadioCardRoot>
                    <Field
                      label={t('programs.modals.editRewards.firstOrderCommissionValue')}
                      invalid={!!formState.errors.firstOrderCommissionValue}
                      errorText={formState.errors.firstOrderCommissionValue?.message}
                    >
                      <InputGroup
                        w="full"
                        endElement={
                          <Text fontWeight="bold">
                            {firstOrderCommissionType === CommissionTypes.Percentage
                              ? '%'
                              : currency}
                          </Text>
                        }
                      >
                        <Input
                          variant="outline"
                          type="number"
                          rounded="lg"
                          min="0"
                          max={
                            firstOrderCommissionType === CommissionTypes.Percentage
                              ? 100
                              : undefined
                          }
                          step="any"
                          {...register('firstOrderCommissionValue', {
                            setValueAs: (value) => (value === '' ? undefined : Number(value)),
                          })}
                        />
                      </InputGroup>
                    </Field>
                    <Alert
                      status="info"
                      variant="surface"
                      icon={<InfoCircle width={16} height={16} />}
                    >
                      {t(
                        isCustomerReward
                          ? 'programs.modals.editRewards.customerFirstOrderEligibility'
                          : 'programs.modals.editRewards.firstOrderCommissionEligibility'
                      )}
                    </Alert>
                    {!isCustomerReward ? (
                      <Text fontSize="sm" color="gray.500">
                        {t('programs.modals.editRewards.firstOrderCommissionOverride')}
                      </Text>
                    ) : null}
                  </VStack>
                ) : null}
              </Box>
            ) : null}
          </VStack>
        </form>
      </FormProvider>
    );
  }
);

export default ProgramRewardsForm;
